import { AddressTypes, CountryAlpha2Codes } from "../enums/data.enums";
import { CountryModel } from "../models/data/country.model";
import { CreateAddressRequest, VerifiedAddressModel } from "../models/data/address.model";
import { StateProvinceModel } from "../models/data/state-province.model";
import { AddressVerificationSide } from "../models/ui/address-verification-prompt.model";
import { CommonChecks } from "./common-checks.utilities";

export class AddressUtilities {

    public static hasAddressInfo(address: {country?: CountryModel, primary?: string, secondary?: string, city?: string, stateProvince?: StateProvinceModel, primaryZip?: string}) : boolean {
        return !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.country) ||
            !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.primary) ||
            !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.secondary) ||
            !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.city) ||
            !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.stateProvince) ||
            !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.primaryZip);
    }

    public static getPrimaryZip(zipPostal: string, countryAlpha2Code?: string): string | null {
        if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(zipPostal)) {
            return null;
        }
        if (countryAlpha2Code === CountryAlpha2Codes.UnitedStatesOfAmerica) {
            return zipPostal.substring(0, 5);
        }
        return zipPostal;
    }

    public static getSecondaryZip(zipPostal: string, countryAlpha2Code?: string): string | null {
        if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(zipPostal)) {
            return null;
        }
        if (countryAlpha2Code === CountryAlpha2Codes.UnitedStatesOfAmerica) {
            const digitsOnly = zipPostal.replace(/\D/g, '');
            if (digitsOnly.length > 5) {
                const secondaryZip = digitsOnly.substring(5);
                return secondaryZip.length > 0 ? secondaryZip : null;
            }
            return null;
        }
        return null;
    }

    // The inverse of getPrimaryZip/getSecondaryZip: the single zipPostal field the address form
    // holds. Unhyphenated, which is what the US mask expects to be handed.
    public static combineZip(primaryZip?: string, secondaryZip?: string): string {
        return secondaryZip ? `${primaryZip ?? ''}${secondaryZip}` : primaryZip ?? '';
    }

    // The postal lines of one side of a verification, laid out as the Address pipe lays out a saved
    // address so the comparison reads the same as the property page it is about to write to.
    public static formatVerifiedLines(side: AddressVerificationSide): string[] {
        const address = side.address;
        const lines = [address.primaryAddress];

        if (!CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(address.secondaryAddress)) {
            lines.push(address.secondaryAddress!);
        }

        const region = side.stateProvince?.abbreviation || side.stateProvince?.name;
        const cityLine = [address.city, region].filter(part => !!part).join(', ');
        const zip = address.secondaryZip ? `${address.primaryZip}-${address.secondaryZip}` : address.primaryZip;
        lines.push(`${cityLine} ${zip}`.trim());

        if (side.country?.name) {
            lines.push(side.country.name);
        }

        return lines.filter(line => !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(line));
    }

    // Only what CSS collapses: space, tab and the line breaks. A non-breaking or em space draws at
    // its own width, so folding those in would hide a difference that is on screen.
    private static readonly COLLAPSIBLE_WHITESPACE = /[ \t\r\n\f]+/g;
    private static readonly COLLAPSED_EDGE_SPACE = /^ | $/g;

    // Whether two formatted sides read the same on screen. Whitespace is collapsed first, because
    // the browser renders a stray double space as one and a difference nobody can see is not one.
    public static isSameRenderedAddress(left: string[], right: string[]): boolean {
        const collapse = (lines: string[]) => lines.map(line => line
            .replace(AddressUtilities.COLLAPSIBLE_WHITESPACE, ' ')
            .replace(AddressUtilities.COLLAPSED_EDGE_SPACE, ''));
        const leftLines = collapse(left);
        const rightLines = collapse(right);

        return leftLines.length === rightLines.length
            && leftLines.every((line, index) => line === rightLines[index]);
    }

    // True when the only thing the provider changed is adding the ZIP+4 extension. By far the most
    // common correction on a US address, and not worth a side-by-side implying the street was wrong.
    public static isZipPlus4OnlyCorrection(original: VerifiedAddressModel, standardized: VerifiedAddressModel): boolean {
        const unchanged = original.primaryAddress === standardized.primaryAddress
            && (original.secondaryAddress ?? '') === (standardized.secondaryAddress ?? '')
            && original.city === standardized.city
            && original.stateProvinceId === standardized.stateProvinceId
            && original.countryId === standardized.countryId
            && original.primaryZip === standardized.primaryZip;

        return unchanged && !original.secondaryZip && !!standardized.secondaryZip;
    }

    // The seven fields of a verification side, posted unchanged. Nothing is trimmed, re-cased or
    // moved between fields: the API links a saved address to its verification by exact content.
    public static toCreateAddressRequest(address: VerifiedAddressModel, type: AddressTypes): CreateAddressRequest {
        return {
            type: type,
            primaryAddress: address.primaryAddress,
            secondaryAddress: address.secondaryAddress ?? undefined,
            city: address.city,
            stateProvinceId: address.stateProvinceId,
            primaryZip: address.primaryZip,
            secondaryZip: address.secondaryZip ?? undefined
        };
    }

    // Whether two address requests describe the same address, so an edit that touched only the
    // title or lot number does not spend a verification call (DEV-716).
    public static isSameAddressRequest(left: CreateAddressRequest | null, right: CreateAddressRequest | null): boolean {
        if (!left || !right) {
            return left === right;
        }

        return left.type === right.type
            && left.primaryAddress === right.primaryAddress
            && (left.secondaryAddress ?? '') === (right.secondaryAddress ?? '')
            && left.city === right.city
            && left.stateProvinceId === right.stateProvinceId
            && left.primaryZip === right.primaryZip
            && (left.secondaryZip ?? '') === (right.secondaryZip ?? '');
    }

}
